import React, {
  ComponentProps,
  ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { sum, get, debounce } from 'lodash';

import { Grid } from '@zapier/design-system';

import Category from './Category';
import EventFilter from './EventFilter';
import Graph from './Graph';
import Period from './Period';
import { colorsMapRgb } from './constants';

import type { EventFiltersType } from './MonitoringPage';
import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  appId: number;
  appVersion?: SemVer;
  clearAppEvents: () => null;
  clearErrors: () => null;
  hasAcceptedTerms: boolean;
  isLoading: boolean;
  isStaff: boolean;
  loadAppEvents: (filters: EventFiltersType) => null;
  loadAppEventStats: (filters: EventFiltersType) => null;
  stats: ComponentProps<typeof Graph>['data'];
  trackPageEvent: ({
    event: string,
    event_data: { app_id: number, filters: EventFiltersType },
  }) => null;
};

function MonitorGraph({
  appId,
  appVersion,
  clearAppEvents,
  clearErrors,
  hasAcceptedTerms,
  isLoading,
  isStaff,
  loadAppEvents,
  loadAppEventStats,
  stats,
  trackPageEvent,
}: Props) {
  const [category, setCategory] = useState<string>('all');
  const [isAnimated, setIsAnimated] = useState<boolean>(true);
  const [otherEmail, setOtherEmail] = useState<string>('');
  const [periodIndex, setPeriodIndex] = useState<number>(NaN);
  const [periodToShow, setPeriodToShow] = useState<string>('1h');

  const colors = {
    '2xx': colorsMapRgb['2xx'],
    '3xx': colorsMapRgb['3xx'],
    '4xx': colorsMapRgb['4xx'],
    '5xx': colorsMapRgb['5xx'],
    bundle: colorsMapRgb.bundle,
    errors: colorsMapRgb.errors,
    hooks: colorsMapRgb.hooks,
    ...(appVersion ? { console: colorsMapRgb.console } : {}),
  };

  const categories: Array<{
    count: number;
    key: string;
    label: string;
  }> = appVersion
    ? [
        ['2xx', '2xx Requests'],
        ['3xx', '3xx Requests'],
        ['4xx', '4xx Requests'],
        ['5xx', '5xx Requests'],
        ['hooks', 'Hooks'],
        ['errors', 'Errors'],
        ['bundle', 'Bundle'],
        ...(appVersion ? [['console', 'Console']] : []),
      ].map(([key, label]) => ({
        count: Number(!isLoading && sum(stats?.datasets?.[key] ?? [])),
        key,
        label,
      }))
    : [];

  const period = isNaN(periodIndex)
    ? null
    : {
        start: get(stats, ['periods', `${periodIndex}`, 'start_date']),
        end: get(stats, ['periods', `${periodIndex}`, 'end_date']),
      };

  const loadData = (
    overrides: Partial<{
      category: string;
      otherEmail: string;
      periodIndex: number;
    }> = {}
  ) => {
    if (!appVersion) return;

    const filters: EventFiltersType = {
      app_version: appVersion,
      category: overrides.category ?? category,
      other_email: overrides.otherEmail ?? otherEmail,
      start_date:
        stats.periods[
          isNaN(overrides.periodIndex) ? periodIndex : overrides.periodIndex
        ].start_date,
      end_date:
        stats.periods[
          isNaN(overrides.periodIndex) ? periodIndex : overrides.periodIndex
        ].end_date,
    };

    trackPageEvent({
      event: 'Fetching event list',
      event_data: { app_id: appId, filters },
    });
    clearErrors();
    loadAppEvents(filters);
  };

  const debounceLoadData = debounce(loadData, 500);

  const onClickGraph = (index: number) => {
    setIsAnimated(false);
    setPeriodIndex(index);
    loadData({ periodIndex: index });
    return undefined;
  };

  const loadStats = useCallback(
    (periodNext: string) => {
      if (!appVersion) return;

      clearAppEvents();
      clearErrors();

      const filters = { appVersion, period: periodNext };
      trackPageEvent({
        event: 'Re-fetching chart data',
        event_data: { app_id: appId, filters },
      });
      loadAppEventStats(filters);
    },
    [
      appId,
      appVersion,
      clearAppEvents,
      clearErrors,
      loadAppEventStats,
      trackPageEvent,
    ]
  );

  const onChangePeriod = item => {
    const periodNext = item.value;
    setIsAnimated(true);
    setPeriodIndex(NaN);
    setPeriodToShow(periodNext);
    loadStats(periodNext);
  };

  const onRefreshGraph = () => {
    setIsAnimated(true);
    loadStats(periodToShow);
  };

  const onChangeCategory = item => {
    const categoryNext = item.value;

    if (categoryNext === category) return;

    setIsAnimated(false);
    setCategory(categoryNext);
    debounceLoadData({ category: categoryNext });
  };

  const onChangeOtherEmail = (e: ChangeEvent<HTMLInputElement>) => {
    const otherEmailNext = e.currentTarget.value;

    if (otherEmailNext === otherEmail) return;

    setIsAnimated(false);
    setOtherEmail(otherEmailNext);
    debounceLoadData({ otherEmail: otherEmailNext });
  };

  useEffect(() => {
    setCategory('all');
    setOtherEmail('');
    setPeriodIndex(NaN);
    setIsAnimated(true);
    clearAppEvents();
    loadStats(periodToShow);
  }, [appVersion, clearAppEvents, loadStats, periodToShow]);

  return (
    <Grid columns={1} gap={30}>
      <Graph
        colors={colors}
        data={stats}
        fillColorTransparent={false}
        isAnimated={isAnimated}
        isLoading={isLoading}
        onClickGraph={onClickGraph}
        onClickRefresh={onRefreshGraph}
        sidebar={
          <Fragment>
            <Period
              onChangePeriod={onChangePeriod}
              periodToShow={periodToShow}
            />
            <Category data={categories} isLoading={isLoading} />
          </Fragment>
        }
      />
      {appVersion && (
        <EventFilter
          appVersion={appVersion}
          category={category}
          email={otherEmail}
          hasAcceptedTerms={hasAcceptedTerms}
          isStaff={isStaff}
          onChangeEmail={onChangeOtherEmail}
          onChangeFilter={onChangeCategory}
          period={period}
        />
      )}
    </Grid>
  );
}

export default MonitorGraph;
